import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";

import PageLayout from "../_layout/PageLayout";
import { MyAccountMenuItemsModel } from "./MyAccountMenuItems";
import { currentUserService } from "../../services/user/CurrentUserStorage";
import { api } from "../../services/api/ApiService";
import { MyOrganizationsStorageModel } from "../../services/organization/MyOrganizationsStorage";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import StopCollaborationButton from "../users/components/buttons/StopCollaborationButton";
import { DeleteOrganizationButton } from "./components/buttons/DeleteOrganizationButton";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { lastAccessedOrganizationService } from "../../services/organization/LastAccessedOrganizationStorage";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import DataGrid from "../../components/datatable/DataGrid";
import { translationService } from "../../services/translation/TranslationService";
import { UserOrganization } from "../../services/user/my-account/UserOrganization";
import dateService from "../../services/date-service/DateService";
import { Info } from "../../components/svg/svgImageList";
import Svg from "../../components/svg/Svg";
import "./composition.scss";
import { UserRolesStorageModel } from "../../services/user/UserRolesStorage";
import { EmploymentStatusesStorageModel } from "../../services/user/EmploymentStatusesStorage";
import { OrganizationStatusesStorageModel } from "../../services/user/OrganizationStatusesStorage";
import { UserRoles } from "../../generated/Users";

export default observer(() => {
  const { handleErrorInSnackbar } = useErrorHandler();

  const [userRolesStorage] = useState(() => UserRolesStorageModel.create({}, { api }));

  const [employmentStatusesStorage] = useState(() =>
    EmploymentStatusesStorageModel.create({}, { api })
  );

  const [organizationStatusesStorage] = useState(() =>
    OrganizationStatusesStorageModel.create({}, { api })
  );

  const [myOrganizationsStorage] = useState(() =>
    MyOrganizationsStorageModel.create(
      {
        useGridFiltering: true,
        autoLoad: false,
      },
      { api }
    )
  );

  const [myAccountMenuItems] = useState(() => MyAccountMenuItemsModel.create({}));

  const menuItems = {
    staticItems: myAccountMenuItems.getStaticItems(),
  };

  useEffect(() => {
    if (
      organizationStatusesStorage.isLoading ||
      userRolesStorage.isLoading ||
      employmentStatusesStorage.isLoading
    ) {
      return;
    }

    myOrganizationsStorage.setOrganizationStatusOptions(
      toJS(organizationStatusesStorage.organizationStatuses)
    );
    myOrganizationsStorage.setUserRoleOptions(toJS(userRolesStorage.userRoles));
    myOrganizationsStorage.setEmploymentStatusOptions(
      toJS(employmentStatusesStorage.employmentStatuses)
    );

    async function load() {
      await myOrganizationsStorage.load();
    }

    load();
  }, [
    employmentStatusesStorage.employmentStatuses,
    employmentStatusesStorage.isLoading,
    myOrganizationsStorage,
    organizationStatusesStorage.isLoading,
    organizationStatusesStorage.organizationStatuses,
    userRolesStorage.isLoading,
    userRolesStorage.userRoles,
  ]);

  const organizationActionsDisabled = useMemo(
    () =>
      !currentUserService.userIsPartOfAnOrganization || !myOrganizationsStorage.anyItemsSelected,
    [myOrganizationsStorage.anyItemsSelected]
  );

  const organizationDeleteButtonDisabled = useMemo(
    () =>
      !currentUserService.userIsPartOfAnOrganization ||
      !myOrganizationsStorage.anyItemsSelected ||
      myOrganizationsStorage.roleIdOfSelectedItem !== UserRoles.Admin,
    [myOrganizationsStorage.anyItemsSelected, myOrganizationsStorage.roleIdOfSelectedItem]
  );

  const reloadPermissionsAndLastAccessedOrganization = useCallback(async () => {
    await lastAccessedOrganizationService.load();
    await permissionsService.reloadPermissions();
  }, []);

  const handleRemoveUser = async () => {
    try {
      await myOrganizationsStorage.stopUserCollaboration(myOrganizationsStorage.selectedItems[0]);
      await reloadPermissionsAndLastAccessedOrganization();
      snackbarNotificationsService.showSuccess("MyOrganizations.stop-collaboration.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.user.stop-own-collaboration");
      handleErrorInSnackbar(applicationError);
    }
  };

  const handleOrganizationDelete = async (organizationId: number) => {
    await myOrganizationsStorage.requestDeleteOrganization(organizationId);

    if (lastAccessedOrganizationService.data.organizationId === organizationId) {
      window.location.href = "/";
    } else {
      await myOrganizationsStorage.load();
      await currentUserService.load();
    }
  };

  return (
    <PageLayout
      titleKey="MyAccount.organizations"
      id="view-organizations"
      menuItems={menuItems}
      isLoading={currentUserService.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <StopCollaborationButton
            disabled={organizationActionsDisabled}
            confirmationAction={handleRemoveUser}
            confirmationModalTitleKey="MyOrganizations.stop-collaboration.confirmation.title"
            confirmationModalTitleContext={{
              organizationName: myOrganizationsStorage.nameOfSelectedItem,
            }}
            confirmationModalSubtitleKey="MyOrganizations.stop-collaboration.confirmation"
            optionalWarningKey={
              myOrganizationsStorage.lockedInspectionsCountOfSelectedItem > 0
                ? myOrganizationsStorage.lockedInspectionsCountOfSelectedItem > 1
                  ? "MyOrganizations.stop-collaboration.confirmation.locked-inspections"
                  : "MyOrganizations.stop-collaboration.confirmation.locked-inspection"
                : undefined
            }
            optionalWarningContext={
              myOrganizationsStorage.lockedInspectionsCountOfSelectedItem > 0
                ? { count: myOrganizationsStorage.lockedInspectionsCountOfSelectedItem }
                : {}
            }
          />
          <DeleteOrganizationButton
            disabled={organizationDeleteButtonDisabled}
            myOrganizationsStorage={myOrganizationsStorage}
            confirmationAction={handleOrganizationDelete}
          />
        </ButtonsSection>
      </ActionsSection>
      {myOrganizationsStorage.hasItems &&
        myOrganizationsStorage.organizationsWithSubscriptionsCanceled.map((x: UserOrganization) => {
          return (
            <div key={x.id} className="info-notification-container">
              <div>
                <Svg svg={Info} />
              </div>
              <span>
                {translationService.translate("MyOrganizations.canceled-subscription-info", {
                  organizationName: x.name,
                  cancelationDate: dateService.formatDate(x.subscriptionCancelationDate),
                })}
              </span>
            </div>
          );
        })}
      <DataGrid
        itemTypeKey="general.organizations"
        dataGridStorage={myOrganizationsStorage}
        singleSelection
        noResultsMessageKey="MyAccount.organizations.no-results"
      />
    </PageLayout>
  );
});
